import axios, { AxiosInstance } from 'axios';
import React from 'react';
import { useAuthContext } from '../Auth';

const API_BASE_URL = '/api';

const ApiContext = React.createContext<AxiosInstance | null>(null);

export function useApiClient(): AxiosInstance {
  const instance = React.useContext(ApiContext);
  if (instance == null) {
    throw new Error("api client is not inside a provider");
  }
  return instance;
}

interface ApiProviderContextProps {
  children: React.ReactNode;
}

export function ApiProvider(params: ApiProviderContextProps): React.ReactElement {
  const { children } = params;

  const { token } = useAuthContext();
  const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  apiClient.interceptors.request.use(config => {
    try {
      const customerStr = localStorage.getItem('currentCustomer');
      if (customerStr) {
        try {
          const customer = JSON.parse(customerStr);
          if (customer && customer.tenant && customer.environment) {
            config.headers['X-Tenant'] = customer.tenant;
            config.headers['X-Environment'] = customer.environment;
          }
        } catch (error) {
          console.error('Failed to parse customer from localStorage:', error);
        }
      }
    } catch (error) {
      console.error('Failed to parse customer from localStorage:', error);
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 400 && error.response?.data?.code === 'NO_CUSTOMER_CONTEXT') {
        localStorage.removeItem('currentCustomer');
      }
      return Promise.reject(error);
    });

  return <ApiContext.Provider value={apiClient}>{children}</ApiContext.Provider>;
}

